import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShoppingCartIcon,
  PlusIcon,
  MinusIcon,
  ArrowLeftIcon,
  CreditCardIcon,
  UsersIcon,
  ClockIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { groupOrdersApi } from '../utils/advancedOrdersApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';

const GroupOrderMealsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [groupOrder, setGroupOrder] = useState(null);
  const [meals, setMeals] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userBalance, setUserBalance] = useState(0);

  useEffect(() => {
    if (orderId) {
      fetchGroupOrderDetails();
      fetchUserBalance();
    }
  }, [orderId]);

  useEffect(() => {
    if (groupOrder?.restaurant?._id) {
      fetchMeals();
    }
  }, [groupOrder]);

  const fetchGroupOrderDetails = async () => {
    try {
      const response = await groupOrdersApi.getGroupOrderDetails(orderId);
      if (response.success) {
        setGroupOrder(response.data);
      } else {
        showErrorNotification('Failed to load group order details');
        navigate('/user/advanced-orders');
      }
    } catch (error) {
      console.error('Failed to fetch group order:', error);
      showErrorNotification('Failed to load group order');
      navigate('/user/advanced-orders');
    }
  };

  const fetchMeals = async () => {
    setLoading(true);
    try {
      const response = await groupOrdersApi.getRestaurantMeals(groupOrder.restaurant._id);
      if (response.success) {
        setMeals(response.data.meals || []);
      } else {
        showErrorNotification('Failed to load restaurant meals');
      }
    } catch (error) {
      console.error('Failed to fetch meals:', error);
      showErrorNotification('Failed to load restaurant meals');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserBalance = async () => {
    try {
      // console.log("Fetching user balance...");
      const token = localStorage.getItem('token');
      const response = await fetch('https://mongobyte-hc73.onrender.com/api/v1/users/getProfile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      // console.log(data)
      if (data.user) {
        // console.log(data.user)
        setUserBalance(data.user.byteBalance || 0);
      }
    } catch (error) {
      console.error('Failed to fetch user balance:', error);
    }
  };

  const updateMealQuantity = (mealId, change) => {
    setSelectedMeals(prev => {
      const newMap = new Map(prev);
      const currentQuantity = newMap.get(mealId) || 0;
      const newQuantity = Math.max(0, currentQuantity + change);
      
      if (newQuantity === 0) {
        newMap.delete(mealId);
      } else {
        newMap.set(mealId, newQuantity);
      }
      
      return newMap;
    });
  };

  const calculateTotal = () => {
    let total = 0;
    selectedMeals.forEach((quantity, mealId) => {
      const meal = meals.find(m => m._id === mealId);
      if (meal) {
        total += meal.price * quantity;
      }
    });
    return total;
  };

  const handleAddMealsAndPay = async () => {
    if (selectedMeals.size === 0) {
      showErrorNotification('Please select at least one meal');
      return;
    }

    const total = calculateTotal();
    if (userBalance < total) {
      showErrorNotification(`Insufficient balance. You need ₦${total - userBalance} more.`);
      return;
    }

    setSubmitting(true);
    try {
      // First add meals to group order
      const mealsToAdd = Array.from(selectedMeals.entries()).map(([mealId, quantity]) => ({
        meal: mealId,
        quantity
      }));

      const addMealsResponse = await groupOrdersApi.addMealsToGroupOrder(orderId, mealsToAdd);
      
      if (!addMealsResponse.success) {
        throw new Error(addMealsResponse.message || 'Failed to add meals to group order');
      }

      // Then process payment
      const paymentResponse = await groupOrdersApi.payForGroupOrder(orderId);
      
      if (paymentResponse.success) {
        showSuccessNotification('Meals added and payment processed successfully!');
        navigate('/user/advanced-orders');
      } else {
        showErrorNotification(paymentResponse.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Failed to add meals and pay:', error);
      showErrorNotification(error.message || 'Failed to process order');
    } finally {
      setSubmitting(false);
    }
  };

  const getTimeLeft = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate - now;
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h left`;
    }
    
    return `${hours}h ${minutes}m left`;
  };

  if (!groupOrder) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni"></div>
            <span className="ml-3 text-olive-light font-sans">Loading group order...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/user/advanced-orders')}
            className="flex items-center gap-2 text-olive-light hover:text-pepperoni mb-4 font-sans"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Group Orders
          </button>
          
          <div className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-crust font-display mb-2">
                  {groupOrder.title}
                </h1>
                <p className="text-olive-light font-sans mb-2">{groupOrder.description}</p>
                <p className="text-lg font-bold text-pepperoni">{groupOrder.restaurant.name}</p>
              </div>
              <div className="text-right">
                <div className="bg-primary-50 px-3 py-1 rounded-full border border-pepperoni/20 mb-2">
                  <span className="text-sm font-bold text-pepperoni">
                    {getTimeLeft(groupOrder.orderDeadline)}
                  </span>
                </div>
                <div className="text-sm text-olive-light">
                  {groupOrder.participants?.length || 0}/{groupOrder.maxParticipants} participants
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <MapPinIcon className="w-4 h-4 text-pepperoni" />
                <span className="text-olive-light">{groupOrder.deliveryInfo?.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-pepperoni" />
                <span className="text-olive-light">
                  Delivery: {new Date(groupOrder.deliveryTime).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary-50 rounded-xl p-4 border-2 border-primary-200 mb-6"
        >
          <h4 className="font-bold text-primary-800 font-display mb-2">Select Your Meals</h4>
          <div className="text-sm text-primary-700 font-sans space-y-1">
            <p>• Choose the meals you want from {groupOrder.restaurant.name}</p>
            <p>• Your selections will be added to the group order</p>
            <p>• Payment will be processed immediately</p>
            <p>• You can chat with other participants after ordering</p>
          </div>
        </motion.div>

        {/* Meals Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni"></div>
            <span className="ml-3 text-olive-light font-sans">Loading meals...</span>
          </div>
        ) : meals.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCartIcon className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-crust font-display mb-2">
              No meals available
            </h3>
            <p className="text-olive-light font-sans">
              This restaurant doesn't have any meals available right now.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {meals.map((meal) => {
              const quantity = selectedMeals.get(meal._id) || 0;
              
              return (
                <motion.div
                  key={meal._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-accentwhite rounded-xl p-6 border-2 transition-all duration-300 ${
                    quantity > 0 ? 'border-pepperoni bg-primary-50' : 'border-secondary-200 hover:border-secondary-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {meal.imageUrl && (
                      <img
                        src={meal.imageUrl}
                        alt={meal.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-bold text-crust font-display mb-2">
                        {meal.name}
                      </h4>
                      <p className="text-sm text-olive-light font-sans mb-3 line-clamp-2">
                        {meal.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-pepperoni font-display">
                          ₦{meal.price}
                        </span>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateMealQuantity(meal._id, -1)}
                            disabled={quantity === 0}
                            className="w-10 h-10 rounded-full bg-secondary-200 hover:bg-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200"
                          >
                            <MinusIcon className="w-5 h-5 text-olive-light" />
                          </button>
                          <span className="w-8 text-center font-bold text-crust font-display text-lg">
                            {quantity}
                          </span>
                          <button
                            onClick={() => updateMealQuantity(meal._id, 1)}
                            className="w-10 h-10 rounded-full bg-pepperoni hover:bg-primary-800 text-white flex items-center justify-center transition-all duration-200"
                          >
                            <PlusIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Order Summary & Checkout */}
        {selectedMeals.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 p-6 sticky bottom-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-crust font-display">
                  Order Summary
                </h3>
                <p className="text-sm text-olive-light font-sans">
                  {selectedMeals.size} item{selectedMeals.size !== 1 ? 's' : ''} selected
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-pepperoni font-display">
                  ₦{calculateTotal()}
                </div>
                <div className="text-sm text-olive-light font-sans">
                  Balance: ₦{userBalance}
                </div>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddMealsAndPay}
              disabled={submitting || userBalance < calculateTotal()}
              className={`w-full py-4 px-6 rounded-xl font-bold font-display text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 ${
                submitting || userBalance < calculateTotal()
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-600 text-white'
              }`}
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : userBalance < calculateTotal() ? (
                <>
                  <CreditCardIcon className="w-5 h-5" />
                  <span>Insufficient Balance</span>
                </>
              ) : (
                <>
                  <CreditCardIcon className="w-5 h-5" />
                  <span>Add to Order & Pay ₦{calculateTotal()}</span>
                </>
              )}
            </motion.button>

            {userBalance < calculateTotal() && (
              <p className="text-center text-red-600 text-sm font-sans mt-2">
                You need ₦{calculateTotal() - userBalance} more to complete this order
              </p>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default GroupOrderMealsPage;